import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { isServiceWorkerBlockedHost } from "@/lib/swHostGuard";

/**
 * Registers the app's service worker (generated by vite-plugin-pwa) and
 * shows a toast with a "Refresh" action when a new SW version is waiting.
 *
 * Dismissing the toast does NOT cancel the update — `registerType: "autoUpdate"`
 * still applies it on the next natural full reload. The prompt is an
 * opt-in faster path.
 *
 * Skips entirely on Lovable preview / localhost / `?sw=off` (same guard as
 * the deferred loader in index.html) and in non-production builds.
 */
export function useServiceWorkerUpdate() {
  const registeredRef = useRef(false);

  useEffect(() => {
    if (registeredRef.current) return;
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator)) return;
    if (isServiceWorkerBlockedHost()) return;
    if (!import.meta.env.PROD) return;

    registeredRef.current = true;

    let cancelled = false;
    const timer = window.setTimeout(() => {
      if (cancelled) return;
      void (async () => {
        try {
          const { registerSW } = await import("virtual:pwa-register");
          const updateSW = registerSW({
            immediate: true,
            onNeedRefresh() {
              toast("New version available", {
                description: "Refresh to get the latest BailBuddies update.",
                duration: Infinity,
                action: {
                  label: "Refresh",
                  onClick: () => {
                    // skipWaiting + reload once the new SW takes control.
                    void updateSW(true);
                  },
                },
              });
            },
            onRegisterError(err) {
              if (import.meta.env.DEV) {
                console.warn("[sw] registration failed", err);
              }
            },
          });
        } catch (err) {
          if (import.meta.env.DEV) {
            console.warn("[sw] virtual:pwa-register unavailable", err);
          }
        }
      })();
    }, 3000);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, []);
}
